import {
  MouseEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "stores";
import styled from "styled-components";
import store from "stores/interfaces";
import {
  Tabs,
  IconButton,
  TabScrollButton,
  TabScrollButtonProps,
  Typography,
  Popover,
  MenuList,
  MenuItem,
  Divider,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useTranslation } from "react-i18next";
import {
  getUserMediumDirectories,
  getUserMediumDirectory,
} from "stores/modules/entities.user.mediumDirectories/selectors";
import {
  addUserMediumDirectory,
  removeUserMediumDirectory,
  setInitialDirectories,
  updateUserMediumDirectory,
} from "stores/modules/entities.user.mediumDirectories/operations";
import { useForm } from "react-hook-form";
import {
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import MediumListLibrarySortableTab from "./MediumListLibrarySortableTab";

const LIMIT_TABS = 10;

const MediumListLibraryTabs = () => {
  const d = useAppDispatch();
  const [t] = useTranslation();

  const tab = useAppSelector((state) => {
    return store.getAppState(state, "userMediumListLibraryTab");
  });
  const tabIds = useAppSelector(getUserMediumDirectory);
  const tabs = useAppSelector((state) => getUserMediumDirectories(state));

  const [menuTabId, setMenuTabId] = useState<string | null>(null);
  const anchorEl = useRef<Element | null>(null);

  const [edittingNameTabId, setEdittingNameTabId] = useState<string | null>(
    null
  );
  const [isDragging, setIsDragging] = useState(false);
  const selectingMedia = useAppSelector((state) => {
    return store.getAppState(state, "selectingMedia");
  });
  const [scrollButtons, setScrollButtons] = useState<boolean | "auto">("auto");

  useEffect(() => {
    if (tabs.length === 0) {
      d(setInitialDirectories());
    }
  }, [tabs.length, d]);

  useEffect(() => {
    if (!tabIds.includes(tab) && tabIds.length > 0) {
      d(store.focusMediumDirectory(tabIds[0]));
    }
  }, [tab, tabIds, d]);

  const onChange = useCallback(
    (_event, value) => {
      if (edittingNameTabId === value) {
        return;
      }

      d(store.focusMediumDirectory(value));
    },
    [d, edittingNameTabId]
  );

  const onClickTab = useCallback(
    (value: string) => (event: MouseEvent) => {
      if (edittingNameTabId === value) {
        return;
      }

      if (value === tab) {
        setMenuTabId(value);
        anchorEl.current = event.currentTarget;
      }
    },
    [tab, edittingNameTabId, anchorEl, setMenuTabId]
  );

  const addDirectory = useCallback(async () => {
    const id = await d(addUserMediumDirectory());
    if (id) {
      d(store.focusMediumDirectory(id));
    }
  }, [d]);

  const onCloseMenuPopover = useCallback(() => {
    setMenuTabId(null);
  }, [setMenuTabId]);

  const disabledAddButton = tabIds.length >= LIMIT_TABS;
  const labelAddButton = selectingMedia
    ? t("選択中はタブを追加できません")
    : disabledAddButton
    ? t("タブ数の上限に達しました")
    : t("タブを追加");

  return (
    <SortableContext
      items={tabs}
      id="bgm-tab"
      strategy={horizontalListSortingStrategy}
    >
      <Tabs
        value={isDragging ? null : tab}
        onChange={onChange}
        variant="scrollable"
        scrollButtons={scrollButtons}
        ScrollButtonComponent={(props) =>
          ScrollButton({ ...props, isDragging })
        }
      >
        {tabs.map(({ id, name }) => {
          const editting = edittingNameTabId === id;
          const label = editting ? (
            <TabLabelInput
              id={id}
              name={name}
              onEdited={() => {
                setScrollButtons("auto");
                setEdittingNameTabId(null);
              }}
            />
          ) : (
            <TabLabel name={name} selected={tab === id} />
          );
          const selectedStyle = {
            ...(selectingMedia && tab === id ? { opacity: 0.5 } : {}),
          };

          return (
            <MediumListLibrarySortableTab
              key={id}
              label={label}
              value={id}
              onClick={onClickTab(id)}
              disableRipple={editting}
              id={id}
              setIsDragging={setIsDragging}
              disabled={selectingMedia}
              settingStyle={selectedStyle}
              isDisabled={!!edittingNameTabId}
            />
          );
        })}
        <Tooltip title={labelAddButton}>
          <span>
            <IconButton
              onClick={addDirectory}
              disabled={disabledAddButton || selectingMedia}
              size="large"
            >
              <AddIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Tabs>
      <MenuPopover
        directoryId={menuTabId}
        anchorEl={anchorEl.current}
        onClose={onCloseMenuPopover}
        onRequestEdit={setEdittingNameTabId}
        setScrollButtons={setScrollButtons}
      />
    </SortableContext>
  );
};

type TabLabelProps = {
  name: string;
  selected: boolean;
};

const TabLabel = ({ name, selected }: TabLabelProps) => {
  const [t] = useTranslation();

  return (
    <InnerTab style={{ width: "100%" }}>
      <Typography variant="inherit" noWrap style={{ width: "100%" }}>
        {name || t("BGM")}
      </Typography>
      {selected && (
        <ArrowDropDownIcon fontSize="small" style={{ marginRight: "-12px" }} />
      )}
    </InnerTab>
  );
};

type TabLabelInputProps = {
  id: string;
  name: string;
  onEdited: () => void;
};

const TabLabelInput = ({ id, name, onEdited }: TabLabelInputProps) => {
  const dispatch = useAppDispatch();
  const { register, handleSubmit } = useForm({
    mode: "onBlur",
    defaultValues: {
      name,
    },
  });

  const onSubmit = ({ name }: { name: string }, event: any) => {
    event.stopPropagation();
    if (focused.current) {
      dispatch(updateUserMediumDirectory(id, { name }));
      ref.current?.blur();
      onEdited();
    }
  };

  const focused = useRef<boolean>(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setTimeout(() => {
      ref.current?.focus();
      focused.current = true;
    }, 0);
  }, [ref, focused]);

  return (
    <InnerTab
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
      }}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        onBlur={handleSubmit(onSubmit)}
        autoComplete="off"
        autoCorrect="off"
      >
        <TextField
          inputProps={{ style: { padding: "8px" } }}
          variant="outlined"
          size="small"
          inputRef={ref}
          margin="none"
          {...register("name")}
        />
      </form>
    </InnerTab>
  );
};

interface CustomScrollButtonProps extends TabScrollButtonProps {
  isDragging: boolean;
}

const ScrollButton = (props: CustomScrollButtonProps) => {
  const { isDragging, ...otherProps } = props;
  const onHover = (event: MouseEvent<HTMLButtonElement>) => {
    if (isDragging && otherProps.onClick) {
      otherProps.onClick(event);
    }
  };

  return <TabScrollButton onMouseEnter={onHover} {...otherProps} />;
};

type MenuPopoverProps = {
  directoryId: string | null;
  anchorEl: Element | null;
  onClose: () => void;
  onRequestEdit: (directoryId: string) => void;
  setScrollButtons: (scrollButtons: boolean) => void;
};

const MenuPopover = ({
  directoryId,
  anchorEl,
  onClose,
  onRequestEdit,
  setScrollButtons,
}: MenuPopoverProps) => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const tabLength = useAppSelector(
    (state) => store.getUserMediumDirectory(state).length
  );
  const isLastOne = tabLength <= 1;
  const index = useAppSelector((state) =>
    store.getUserMediumDirectory(state).indexOf(directoryId || "")
  );

  const deleteDirectory = useCallback(() => {
    if (
      directoryId &&
      !isLastOne &&
      window.confirm(
        t(
          "このタブを本当に削除しますか？ 削除するとタブに含まれるファイルも全て削除されます。"
        )
      )
    ) {
      onClose();
      dispatch(removeUserMediumDirectory(directoryId));
    }
  }, [directoryId, isLastOne, onClose, dispatch, t]);

  const requestEditName = useCallback(() => {
    if (directoryId) {
      setScrollButtons(true);
      onClose();
      onRequestEdit(directoryId);
    }
  }, [directoryId, onRequestEdit, onClose, setScrollButtons]);

  const moveLeft = useCallback(() => {
    dispatch(store.swapUserMediumDirectory(index, index - 1));
  }, [index, dispatch]);
  const moveRight = useCallback(() => {
    dispatch(store.swapUserMediumDirectory(index, index + 1));
  }, [index, dispatch]);

  const canMoveRight = index < tabLength - 1;

  return (
    <Popover
      open={directoryId != null}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <MenuList>
        <MenuItem onClick={requestEditName}>{t("名前を変更")}</MenuItem>
        <MenuItem onClick={deleteDirectory} disabled={isLastOne}>
          {t("削除")}
        </MenuItem>
      </MenuList>
      <Divider />
      <MenuList>
        <MenuItem onClick={moveLeft} disabled={index === 0}>
          {t("左に移動")}
        </MenuItem>
        <MenuItem onClick={moveRight} disabled={!canMoveRight}>
          {t("右に移動")}
        </MenuItem>
      </MenuList>
    </Popover>
  );
};

const InnerTab = styled.span`
  display: flex;
  flex-wrap: none;
  align-items: center;
  text-transform: none;
`;

export default memo(MediumListLibraryTabs);
